/**
 * F-030 — Achievements registry.
 *
 * Each achievement defines a predicate over the running game state
 * (score / merges / events) plus a unique id. The registry watches the
 * Score subscriber + game-over events; when a predicate flips true, the
 * achievement unlocks (idempotent — set membership). State persists via
 * KVStorage (LocalStorage by default).
 */

import {
  createMemoryStorage,
  type KVStorage,
} from "../persistence/high-score-store.ts";
import type { Score, ScoreSnapshot } from "./score.ts";

const STORAGE_KEY = "cosmic-suika.achievements.v1";

export interface AchievementContext {
  score: ScoreSnapshot;
  gameOver: boolean;
}

export interface Achievement {
  id: string;
  title: string;
  description: string;
  evaluate(ctx: AchievementContext): boolean;
}

export interface AchievementUnlock {
  id: string;
  unlockedAt: number;
}

export const BUILT_IN_ACHIEVEMENTS: ReadonlyArray<Achievement> = [
  {
    id: "first-merge",
    title: "Stellar Spark",
    description: "Merge your first two planets.",
    evaluate: (ctx) => ctx.score.merges >= 1,
  },
  {
    id: "score-1k",
    title: "Cluster Builder",
    description: "Reach 1,000 points in a single run.",
    evaluate: (ctx) => ctx.score.current >= 1_000,
  },
  {
    id: "score-10k",
    title: "Galactic Architect",
    description: "Reach 10,000 points in a single run.",
    evaluate: (ctx) => ctx.score.current >= 10_000,
  },
  {
    id: "merge-25",
    title: "Cosmic Glue",
    description: "Trigger 25 merges in a single run.",
    evaluate: (ctx) => ctx.score.merges >= 25,
  },
  {
    id: "first-fall",
    title: "Singularity Seeker",
    description: "Survive your first game over (everyone has to start somewhere).",
    evaluate: (ctx) => ctx.gameOver,
  },
];

export interface AchievementsRegistry {
  unlocked(): ReadonlyArray<AchievementUnlock>;
  isUnlocked(id: string): boolean;
  evaluate(ctx: AchievementContext): AchievementUnlock[];
  attachToScore(score: Score): () => void;
  reset(): void;
  subscribe(listener: (unlocked: AchievementUnlock[]) => void): () => void;
}

export interface AchievementsOptions {
  storage?: KVStorage;
  storageKey?: string;
  achievements?: ReadonlyArray<Achievement>;
  now?: () => number;
}

function defaultStorage(): KVStorage {
  if (typeof window === "undefined") return createMemoryStorage();
  try {
    return window.localStorage;
  } catch {
    return createMemoryStorage();
  }
}

export function createAchievementsRegistry(
  options: AchievementsOptions = {},
): AchievementsRegistry {
  const storage = options.storage ?? defaultStorage();
  const key = options.storageKey ?? STORAGE_KEY;
  const list = options.achievements ?? BUILT_IN_ACHIEVEMENTS;
  const now = options.now ?? (() => Date.now());

  const unlockedSet = (() => {
    const raw = storage.getItem(key);
    if (!raw) return new Map<string, AchievementUnlock>();
    try {
      const parsed = JSON.parse(raw) as AchievementUnlock[];
      return new Map(parsed.map((u) => [u.id, u]));
    } catch {
      return new Map<string, AchievementUnlock>();
    }
  })();
  const listeners = new Set<(unlocks: AchievementUnlock[]) => void>();

  const persist = (): void => {
    try {
      storage.setItem(key, JSON.stringify([...unlockedSet.values()]));
    } catch {
      /* quota — silent */
    }
  };

  const announce = (): void => {
    const snap = [...unlockedSet.values()];
    for (const l of listeners) l(snap);
  };

  const evaluate = (ctx: AchievementContext): AchievementUnlock[] => {
    const newlyUnlocked: AchievementUnlock[] = [];
    for (const a of list) {
      if (unlockedSet.has(a.id)) continue;
      if (a.evaluate(ctx)) {
        const unlock: AchievementUnlock = { id: a.id, unlockedAt: now() };
        unlockedSet.set(a.id, unlock);
        newlyUnlocked.push(unlock);
      }
    }
    if (newlyUnlocked.length > 0) {
      persist();
      announce();
    }
    return newlyUnlocked;
  };

  const attachToScore = (score: Score): (() => void) => {
    return score.subscribe((snap) => {
      evaluate({ score: snap, gameOver: false });
    });
  };

  const reset = (): void => {
    unlockedSet.clear();
    persist();
    announce();
  };

  return {
    unlocked: () => [...unlockedSet.values()],
    isUnlocked: (id) => unlockedSet.has(id),
    evaluate,
    attachToScore,
    reset,
    subscribe: (l) => {
      listeners.add(l);
      l([...unlockedSet.values()]);
      return () => listeners.delete(l);
    },
  };
}

export const __testing = { STORAGE_KEY };
